import React, { useState, useEffect, useContext, Suspense } from "react";
import { Toaster } from 'sonner';

import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useNavigation,
} from "react-router-dom";

// Static imports for Layout/Common
import Navbar from "./components/Navbar/Navbar";
import Sidebar from "./components/Dashboard/Sidebar";
import Header from "./components/Dashboard/Header";
import { Footer } from "./components/footer";
import ProtectedRoute from "./page/ProtectedRoute";
import ErrorPage from "./page/ErrorPage";
import "./App.css";
import LogoLoader from "./components/Loader/LogoLoader";

// Loading Fallback
const LoadingFallback = () => (
  <LogoLoader />
);

// Helper for Lazy Loading with Suspense
const Loadable = (Component) => (props) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component {...props} />
  </Suspense>
);

// Lazy Imports
const Home = Loadable(React.lazy(() => import("./page/Home/Home")));
const Eligible = Loadable(React.lazy(() => import("./page/Eligible/Eligible")));
const CorrectionWorkflow = Loadable(React.lazy(() => import("./page/correction_workflow/CorrectionWorkflow")));
const VerificationOverflow = Loadable(React.lazy(() => import("./page/verification_overflow/VerificationOverflow")));
const FocalPerson = Loadable(React.lazy(() => import("./page/FocalPerson/FocalPerson")));
const Frequentlyquestions = Loadable(React.lazy(() => import("./page/FrequentlyQuestions/Frequentlyquestions")));
const ApplicationStatus = Loadable(React.lazy(() => import("./page/Applicationstatus/ApplicationStatus")));

// Auth Pages
const Login = Loadable(React.lazy(() => import("./page/authentication/Login")));
const Signup = Loadable(React.lazy(() => import("./page/authentication/Signup")));
const EmailVerification = Loadable(React.lazy(() => import("./page/authentication/EmailVerification")));
const Forgetpassword = Loadable(React.lazy(() => import("./page/authentication/Forgetpassword")));
const Resetpassword = Loadable(React.lazy(() => import("./page/authentication/Resetpassword")));
const ForgetPasswordVerify = Loadable(React.lazy(() => import("./page/authentication/ForgetPasswordVerify")));
const OTP = Loadable(React.lazy(() => import("./page/authentication/OTP")));
const NewPassword = Loadable(React.lazy(() => import("./page/authentication/NewPassword")));

// Dashboard Pages
const Dashboard = Loadable(React.lazy(() => import("./page/Dashboard/Dashboard")));
const Profile = Loadable(React.lazy(() => import("./page/Dashboard/Profile/Profile")));
const Application = Loadable(React.lazy(() => import("./page/Dashboard/Application/Application")));
const Asset = Loadable(React.lazy(() => import("./page/Dashboard/Assets/Asset")));
const Apply_now = Loadable(React.lazy(() => import("./page/Dashboard/Apply_now/Apply_now")));


const Layout = () => {
  return (
    <div
      style={{
        backgroundImage: 'url(/public/images/bg.png)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        minHeight: '100vh',

        position: 'relative',
      }}
    >
      <Navbar />
      <Outlet />
      <Footer />
    </div>
  );
};


const DashboardLayout = () => {
  const [openSidebarToggle, setOpenSidebarToggle] = useState(false);

  const OpenSidebar = (value) => {
    if (typeof value === 'boolean') {
      setOpenSidebarToggle(value);
    } else {
      setOpenSidebarToggle((prev) => !prev);
    }
  };


  return (
    <div className="dashboard-layout" style={{ position: "relative" }}>
      {/* Overlay - place this before Sidebar */}
      {openSidebarToggle && (
        <div
          onClick={() => OpenSidebar(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.4)", // Dark transparent background
            zIndex: 998, // Lower than sidebar (which is 999)
          }}
        />
      )}

      {/* Sidebar (z-index: 999) */}
      <Sidebar
        openSidebarToggle={openSidebarToggle}
        OpenSidebar={OpenSidebar}
      />

      {/* Main content */}
      <div
        className="dashboard-content"
        style={{
          zIndex: 1,
          marginLeft:
            openSidebarToggle && window.innerWidth > 768 ? "310px" : "0",
          transition: "margin-left 0.3s ease",
        }}
      >
        <div className="main_content">
          <Header OpenSidebar={OpenSidebar} />
          {/* Suspense is handled by Loadable wrapper on individual components */}
          <Outlet />
        </div>

      </div>

    </div>

  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Home /> },
      { path: "/eligible", element: <Eligible />, errorElement: <ErrorPage /> },
      { path: "/correctionworkflow", element: <CorrectionWorkflow /> },
      { path: "/verificationflow", element: <VerificationOverflow /> },
      {
        path: "/focalperson",
        element: <FocalPerson />,
      },
      {
        path: "/faq",
        element: <Frequentlyquestions />,
      },
      {
        path: "/applicationStatus",
        element: <ApplicationStatus />,
      },
    ],
  },
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Dashboard /> },
      { path: "userdashboard", element: <Dashboard /> },
      { path: "profile", element: <Profile /> },
      { path: "application", element: <Application /> },
      { path: "asset", element: <Asset /> },
      { path: "apply", element: <Apply_now /> },
    ],
  },



  {
    path: "/signup",
    element: <Signup />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/emailverify",
    element: <EmailVerification />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/forget",
    element: <Forgetpassword />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/resetpassword",
    element: <Resetpassword />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/resetpasswordverify",
    element: <ForgetPasswordVerify />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/otp",
    element: <OTP />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/newpassword",
    element: <NewPassword />,
    errorElement: <ErrorPage />,
  },

  {
    path: "/login",
    element: <Login />,
    errorElement: <ErrorPage />,
  },
]
  // , {
  //   basename: "/new"
  // }
);

function App() {
  return (
    <div className="app">
      <RouterProvider router={router} />
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

export default App;
